import { parseAttributionDeliveryResult } from "@coeff/utils";
import React, { useEffect, useRef } from "react";
import styled from "styled-components";

import { config } from "../config";
import { useSentryMountUnmountTransaction, useTrack } from "../utils";

const Container = styled.div`
  iframe {
    display: none;
  }
`;

export const EventAttributionIframe = ({
  email,
  event_name,
  event_props,
}: {
  email: string;
  event_name: string;
  event_props?: object;
}) => {
  useSentryMountUnmountTransaction({ name: "EventAttributionIframe" });

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const track = useTrack();

  /**
   * Record what Meta's Conversions API did with the event this frame fires.
   *
   * Without this the iframe is a write-only pipe: the only place `meta_event_id` exists is inside
   * the frame, and it is the id that matches one event across Events Manager, Amplitude and the
   * delivery acknowledgement. The add-on's copy of this component has done it since ENG-8841; the
   * webapp's conversions (`Purchase`, `first_dashboard_created`) were going unrecorded.
   */
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) {
        return;
      }

      const deliveryResult = parseAttributionDeliveryResult(event);
      if (deliveryResult?.attribution_event_name === event_name) {
        track("attribution_delivery_result", deliveryResult);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // Create URL with properly encoded parameters
  const createIframeSrc = () => {
    const baseUrl = `${config.ATTRIBUTION_FRAME_HOST}/addon_event_frame`;
    const params = new URLSearchParams();

    params.append("email", email);
    params.append("event_name", event_name);

    if (event_props) {
      params.append("event_props", JSON.stringify(event_props));
    }

    return `${baseUrl}?${params.toString()}`;
  };

  return (
    <Container>
      <iframe ref={iframeRef} src={createIframeSrc()} />
    </Container>
  );
};
